import { FC } from "react";
import { useNavigate } from "react-router-dom";

import Button from "../Button";
import Text from "../Text";
import styles from "./styles.module.scss";

type Props = {
    /** Lien vers la page de complétion (ex. /account ou /account/questions) */
    completionPath?: string;
    /** Libellés des champs user manquants (ex. ["Prénom", "Nom"]) */
    missingUserFieldLabels?: string[];
    /** Nombre de questions avec réponse / total questions */
    questionsAnswered?: number;
    questionsTotal?: number;
    /** Pourcentage de complétion 0–100 pour la barre de progression */
    completionPercent?: number;
};

/**
 * Bannière affichée lorsque le profil utilisateur est incomplet
 * (champs user ou questions non renseignés). Affiche les infos manquantes et une barre de progression.
 */
const ProfileIncompleteBanner: FC<Props> = ({
    completionPath = "/account",
    missingUserFieldLabels = [],
    questionsAnswered = 0,
    questionsTotal = 0,
    completionPercent = 50,
}) => {
    const navigate = useNavigate();

    // Affichage marketing : minimum 50 % pour ne pas décourager
    const percent = Math.min(100, Math.max(50, completionPercent));
    const circleR = 15.9;
    const circleLength = 2 * Math.PI * circleR;
    const circleDash = (percent / 100) * circleLength;

    return (
        <div className={styles.banner} role="alert">
            <div className={styles.content}>
                <div className={styles.messageRow}>
                    <span className={styles.attentionIcon} aria-hidden>
                        <svg
                            width="18"
                            height="18"
                            viewBox="0 0 24 24"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                        >
                            <path
                                d="M12 2L1 21h22L12 2z"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinejoin="round"
                            />
                            <path
                                d="M12 9v4M12 17h.01"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                            />
                        </svg>
                    </span>
                    <Text variant="body-bold" className={styles.message}>
                        Complétez votre profil pour profiter pleinement de
                        l&apos;application.
                    </Text>
                </div>
            </div>
            <div className={styles.progressCtaBlock}>
                <div
                    className={styles.progressCircleBox}
                    role="progressbar"
                    aria-valuenow={percent}
                    aria-valuemin={0}
                    aria-valuemax={100}
                >
                    <div className={styles.progressCircleWrap}>
                        <svg className={styles.progressCircle} viewBox="0 0 36 36">
                            <circle
                                className={styles.progressCircleBg}
                                cx="18"
                                cy="18"
                                r="15.9"
                                fill="none"
                                strokeWidth="3"
                            />
                            <circle
                                className={styles.progressCircleFill}
                                cx="18"
                                cy="18"
                                r="15.9"
                                fill="none"
                                strokeWidth="3"
                                strokeDasharray={`${circleDash} ${circleLength}`}
                                strokeLinecap="round"
                                transform="rotate(-90 18 18)"
                            />
                        </svg>
                        <span className={styles.progressLabel}>{percent}%</span>
                    </div>
                    {questionsTotal > 0 && (
                        <span className={styles.questionsHint}>
                            Informations complémentaires {questionsAnswered}/{questionsTotal}
                        </span>
                    )}
                </div>
                <Button
                    size="newSmall"
                    variant="primary"
                    label="Compléter mon profil"
                    onClick={() => navigate(completionPath)}
                    className={styles.cta}
                />
            </div>
        </div>
    );
};

export default ProfileIncompleteBanner;
